/**
 * 2_AsdDDDecision.js — Decision for Autism (step 2 of the Adult Autism DD)
 *
 * The MDT records its consensus on the DSM-5 ASD criteria here, using the
 * granular sub-criteria table (A1-A3, B1-B4, C, D, E) that maps directly to
 * the DSM-5 diagnostic manual structure:
 *
 *   - Domain A (Social Communication): all three of A1, A2, A3 must be met.
 *   - Domain B (Restricted/Repetitive): a minimum of 2 out of 4 (B1-B4).
 *   - Criteria C, D, E: all three must be met.
 *
 * Each criterion has a "Met threshold" toggle (Yes / No). These are captured
 * for the clinical record and surfaced on the review, but they are OPTIONAL —
 * they do not gate progress.
 *
 * A single Met / Not met outcome remains the authoritative decision: it drives
 * the deterministic primary-diagnosis report paragraph and unlocks the rest of
 * the module.
 *
 * Pre-fill: A1-A3 and B1-B4 are individually seeded from the SSI stage's
 * per-criterion evidence data (both life stages must show "Clear evidence" for
 * the toggle to default to Yes). C/D/E start unset. Pre-fill happens once via
 * a lazy initial state, so later context refreshes never clobber clinician
 * edits.
 *
 * Persistence: data.autismDecision.criteria and data.autismDecision.outcome on
 * assessmentStages/diagnosticDecision, written via the debounced
 * updateAssessmentStage API (single-writer effect, like the review pages).
 *
 * On leaving the page (Next, or sidebar navigation) we flush the writes and,
 * if an outcome has been chosen, trigger generation of the deterministic
 * "Diagnostic Decision" report paragraph.
 */

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import ModularSideNavbar from '../../../components/autismModular/ModularSideNavbar';
import ModularStepFooter from '../../../components/autismModular/ModularStepFooter';
import ObservationEvidenceTables from '../12_observation/components/ObservationEvidenceTables';
import { ADULT_ASD_OBSERVATION_TASKS } from '../12_observation/config/observationTasksConfig';
import { SsiEvidenceTable } from './1_AsdDDReview';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useModularSectionExitRegeneration } from '../../../hooks/useModularSectionExitRegeneration';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import {
  getAutismDDStepRoutes,
  getCombinedEmergingDifferentials,
  buildAutismDDNavItems,
  getAutismDDRouteForSectionIndex,
  computeAutismDDProgress,
  isAutismDecisionValid,
} from '../../../utils/autismDDRouting';

/**
 * DSM-5 ASD criteria table structure. Organised into sections with threshold
 * rules matching the diagnostic manual. Each criterion has a long-form
 * description displayed in the table and a short label for the formulation
 * summary. The `ssiDataKey` field links to the SSI stage's per-criterion
 * evidence data for pre-filling.
 */
const ASD_CRITERIA_TABLE = [
  {
    sectionHeader: 'Must meet all three areas:',
    criteria: [
      {
        id: 'A1',
        label: 'A1',
        ssiDataKey: 'A1',
        description: 'Deficits in social-emotional reciprocity (e.g., abnormal social approach, failure of normal back-and-forth conversation, reduced sharing of interests or emotions)',
      },
      {
        id: 'A2',
        label: 'A2',
        ssiDataKey: 'A2',
        description: 'Deficits in nonverbal communicative behaviours used for social interaction (e.g., poorly integrated verbal and nonverbal communication, abnormalities in eye contact, body language, or deficits in understanding/use of gestures)',
      },
      {
        id: 'A3',
        label: 'A3',
        ssiDataKey: 'A3',
        description: 'Deficits in developing, maintaining, and understanding relationships (e.g., difficulties adjusting behaviour to suit various social contexts, difficulties in sharing imaginative play, or in making friends)',
      },
    ],
  },
  {
    sectionHeader: 'Must meet a minimum of 2 out of 4 areas:',
    criteria: [
      {
        id: 'B1',
        label: 'B1',
        ssiDataKey: 'B1',
        description: 'Stereotyped or repetitive motor movements, use of objects, or speech (e.g., simple motor stereotypies, lining up toys, echolalia, idiosyncratic phrases)',
      },
      {
        id: 'B2',
        label: 'B2',
        ssiDataKey: 'B2',
        description: 'Insistence on sameness, inflexible adherence to routines, or ritualised patterns of verbal or nonverbal behaviour (e.g., extreme distress at small changes, difficulties with transitions, rigid thinking patterns)',
      },
      {
        id: 'B3',
        label: 'B3',
        ssiDataKey: 'B3',
        description: 'Highly restricted, fixated interests that are abnormal in intensity or focus (e.g., strong attachment to or preoccupation with unusual objects, excessively circumscribed interests)',
      },
      {
        id: 'B4',
        label: 'B4',
        ssiDataKey: 'B4',
        description: 'Hyper- or hyporeactivity to sensory input or unusual interest in sensory aspects of the environment (e.g., apparent indifference to pain/temperature, adverse response to specific sounds or textures, excessive smelling or touching of objects, visual fascination with lights or movement)',
      },
    ],
  },
  {
    sectionHeader: 'Must meet all three areas:',
    criteria: [
      {
        id: 'C',
        label: 'C',
        ssiDataKey: null,
        description: 'Symptoms must be present in the early developmental period (but may not become fully manifest until social demands exceed limited capacities, or may be masked by learned strategies later in life)',
      },
      {
        id: 'D',
        label: 'D',
        ssiDataKey: null,
        description: 'Symptoms cause clinically significant impairment in social, occupational, or other important areas of current functioning',
      },
      {
        id: 'E',
        label: 'E',
        ssiDataKey: null,
        description: 'These disturbances are not better explained by intellectual disability or global developmental delay. Intellectual disability and ASD frequently co-occur; to make comorbid diagnoses, social communication should be below that expected for general developmental level',
      },
    ],
  },
];

// Flat list of all criterion IDs for iteration during state initialisation.
const ALL_CRITERIA_IDS = ASD_CRITERIA_TABLE.flatMap(s => s.criteria.map(c => c.id));

/**
 * Build a lookup from criterion ID to its SSI data key. Only sub-criteria
 * with a non-null ssiDataKey can be pre-filled from SSI evidence.
 */
const CRITERIA_SSI_KEY_MAP = Object.fromEntries(
  ASD_CRITERIA_TABLE.flatMap(s =>
    s.criteria.filter(c => c.ssiDataKey).map(c => [c.id, c.ssiDataKey])
  )
);

/**
 * Derive a pre-fill boolean for one criterion from the SSI stage's per-criterion
 * evidence data. Returns true only when both childhood and adulthood evidence
 * are "Clear evidence"; false when both are decided but at least one is not
 * clear; null when either life stage is undecided.
 */
function prefillFromSSI(ssiStageData, ssiDataKey) {
  const d = ssiStageData?.[ssiDataKey];
  if (!d?.evidence_childhood || !d?.evidence_adulthood) return null;
  return d.evidence_childhood === 'Clear evidence' && d.evidence_adulthood === 'Clear evidence';
}

export default function AsdDDDecision() {
  const { patientId } = useParams();
  const navigate = usePreviewAwareNavigate();

  const {
    header,
    assessmentStages,
    updateAssessmentStage,
    flushStageWrites,
    reportContent,
  } = useContext(ModularPatientDataContext);

  const isPreviewMode = useIsPreviewMode();
  const canEdit = useCanEditStage(header || {}, 'DD', { previewMode: isPreviewMode });

  const ddStage = assessmentStages?.diagnosticDecision;
  const savedDecision = ddStage?.data?.autismDecision;

  // SSI stage data holds per-criterion evidence (A1, A2, A3, B1-B4) with
  // evidence_childhood / evidence_adulthood values. Used for pre-fill.
  const ssiStageData = assessmentStages?.semiStructuredInterview?.data || {};

  // SSI criteria for the evidence summary table (same source as MDT Review).
  const ssiCriteria = ssiStageData?.criteria || {};

  // Observation evidence inputs for the read-only evidence summary table.
  // When Behavioural Observation is disabled for this workflow (e.g. LMP) the
  // OBS column is dropped, same as on the MDT Review page.
  const { claims } = useAuth();
  const wf = useWorkflowConfig(header, claims);
  const isObservationEnabled = wf?.isObservationEnabled !== false;

  const obsData = assessmentStages?.observation?.data || {};
  const obsSelectedTaskIds = obsData.selectedTaskIds || [];
  const obsAllTaskData = obsData.allTaskData || {};
  const obsAdosData = obsData.adosData || null;
  const obsCriteriaEvidence = obsData.criteriaEvidence || null;
  const obsCriteriaNotes = obsData.criteriaNotes || {};

  // Lazy initial state — prefer the DD's own saved criteria; otherwise seed
  // from SSI per-criterion evidence. Pre-fill happens once so context
  // refreshes never overwrite in-progress clinician edits.
  const [criteria, setCriteria] = useState(() => {
    const saved = savedDecision?.criteria || {};
    const seed = {};
    for (const id of ALL_CRITERIA_IDS) {
      if (saved[id] !== undefined) {
        seed[id] = saved[id];
      } else {
        const ssiKey = CRITERIA_SSI_KEY_MAP[id];
        seed[id] = ssiKey ? prefillFromSSI(ssiStageData, ssiKey) : null;
      }
    }
    return seed;
  });

  const [outcome, setOutcome] = useState(() => savedDecision?.outcome ?? null);

  const union = useMemo(
    () => getCombinedEmergingDifferentials(assessmentStages),
    [assessmentStages],
  );
  // When skipAutismDifferentialsDecisions is true (TOC), the Other Conditions
  // step is removed — emerging differentials appear on the Formulation page.
  const hasOther = !wf.skipAutismDifferentialsDecisions && union.length > 0;

  // ── Status promotion (notStarted -> inProgress on first write) ──────────────
  const hasStartedRef = useRef(false);
  const maybePromoteStatus = (updates) => {
    if (!hasStartedRef.current && ddStage?.status === 'notStarted') {
      hasStartedRef.current = true;
      return { ...updates, status: 'inProgress' };
    }
    return updates;
  };

  // ── Dirty tracking ───────────────────────────────────────────────────────────
  // Drives ProformaNavigationGuardContext (leaving-section dialog, beforeunload)
  // and the shared exit-regen hook's unmount safety net.
  const [hasChanges, setHasChanges] = useState(false);

  // ── Single-writer persistence effect ───────────────────────────────────────
  // Mirrors the review pages: one hydration-guarded effect persists criteria +
  // outcome + the recomputed progress fraction. Progress never regresses below 1
  // once the stage is completed.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!hydratedRef.current) { hydratedRef.current = true; return; }
    if (!canEdit) return;

    const nextData = { ...(ddStage?.data || {}), autismDecision: { criteria, outcome } };
    let progress = computeAutismDDProgress({ data: nextData, union, status: ddStage?.status });
    if (ddStage?.status === 'completed' && progress < 1) progress = 1;

    updateAssessmentStage('diagnosticDecision', maybePromoteStatus({
      'data.autismDecision.criteria': criteria,
      'data.autismDecision.outcome': outcome,
      progress,
    }));
    setHasChanges(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, criteria, outcome]);

  const handleToggleCriterion = (id, value) => {
    if (!canEdit) return;
    setCriteria(prev => ({ ...prev, [id]: prev[id] === value ? null : value }));
  };

  const handleSetOutcome = (value) => {
    if (!canEdit) return;
    setOutcome(prev => (prev === value ? null : value));
  };

  const isValid = isAutismDecisionValid({ outcome });

  // ── Exit-regeneration via shared hook ────────────────────────────────────────
  // Replaces the hand-rolled unmount net. The hook registers dirty state with
  // ProformaNavigationGuardContext (leaving-section dialog + beforeunload) and
  // fires TriggerGenerateDocumentSection on unmount when dirty or not yet
  // generated — unless markRegenHandled() was called by a managed exit handler.
  const { markRegenHandled } = useModularSectionExitRegeneration({
    patientId,
    stageKey: 'diagnosticDecision',
    sectionTitle: 'Diagnostic Decision',
    hasChanges,
    canEdit,
    reportContent,
    shouldRegenerate: isValid,
  });

  const flushAndGenerate = async () => {
    markRegenHandled();
    const dataReady = flushStageWrites('diagnosticDecision');
    if (canEdit && isValid) {
      TriggerGenerateDocumentSection(
        patientId, 'Autism Diagnostic Decision', 'Diagnostic Decision', [], dataReady,
      );
    }
    await dataReady;
  };

  const { items: navItems, orderedIds } = useMemo(
    () => buildAutismDDNavItems({ assessmentStages, currentSectionIndex: 1, skipDifferentialsDecisions: wf.skipAutismDifferentialsDecisions }),
    [assessmentStages, wf.skipAutismDifferentialsDecisions],
  );

  const navigateToSection = async (index) => {
    await flushAndGenerate();
    navigate(getAutismDDRouteForSectionIndex(patientId, index, hasOther));
  };

  const handleNext = async () => {
    await flushAndGenerate();
    const routes = getAutismDDStepRoutes(patientId);
    navigate(hasOther ? routes.otherConditions : routes.finalFormulation);
  };

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    ['Diagnostic Decision', getAutismDDStepRoutes(patientId).mdtReview],
    ['Decision for Autism'],
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      <div className="flex h-[calc(100vh-8rem)]">
        <ModularSideNavbar
          items={navItems}
          activeId="autismDecision"
          onSelect={(id) => navigateToSection(orderedIds.indexOf(id))}
        />

        <div className="flex-1 flex flex-col min-h-0">
          {isPreviewMode && <PreviewModeBanner />}
          <div className="flex-1 overflow-y-auto p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Decision for Autism</h1>
            <p className="text-gray-500 text-sm mt-1">
              Record the MDT consensus on the DSM-5 ASD criteria and the overall outcome.
            </p>
          </div>

          {/* Two evidence tables need the same width as MDT Review so the
              side-by-side pills stay readable. A single interview table
              (e.g. LMP, no Observation) keeps the narrower decision column. */}
          <div className={`${isObservationEnabled ? '' : 'max-w-4xl'} mx-auto`}>
            {/* Evidence summary tables — same side-by-side layout as the MDT
                Review page, giving clinicians a quick reference for the
                evidence decisions while recording the diagnostic outcome
                without having to flip back. */}
            <div className="mb-8">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 text-center">Evidence Summary</h2>

              {/* Column headers — same cards as MDT Review, centred so the
                  labels read as column titles rather than left-aligned copy. */}
              <div className={`grid grid-cols-1 ${isObservationEnabled ? 'lg:grid-cols-2' : ''} gap-6 mb-4`}>
                <div className="rounded-xl bg-primary/10 border border-primary/20 px-4 py-3 text-center">
                  <h3 className="text-sm font-bold text-primary">Diagnostic Developmental History</h3>
                  <p className="text-xs text-gray-500">History &amp; Semi-Structured Interview</p>
                </div>
                {isObservationEnabled && (
                  <div className="rounded-xl bg-primary/10 border border-primary/20 px-4 py-3 text-center">
                    <h3 className="text-sm font-bold text-primary">Behavioural Observation</h3>
                    <p className="text-xs text-gray-500">Observation appointment</p>
                  </div>
                )}
              </div>

              <div className={`grid grid-cols-1 ${isObservationEnabled ? 'lg:grid-cols-2' : ''} gap-6 items-start`}>
                {/* SSI evidence */}
                <div>
                  <SsiEvidenceTable criteriaData={ssiCriteria} />
                </div>

                {/* OBS evidence — only shown when Behavioural Observation is
                    part of this patient's workflow. */}
                {isObservationEnabled && (
                  <div>
                    {obsSelectedTaskIds.length > 0 ? (
                      <ObservationEvidenceTables
                        tasks={ADULT_ASD_OBSERVATION_TASKS}
                        selectedTaskIds={obsSelectedTaskIds}
                        allTaskData={obsAllTaskData}
                        adosData={obsAdosData}
                        criteriaEvidence={obsCriteriaEvidence}
                        criteriaNotes={obsCriteriaNotes}
                        compact
                      />
                    ) : (
                      <p className="text-sm text-gray-400 italic">No observation evidence recorded.</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* DSM-5 criteria table */}
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 mb-6 text-left">
              <h3 className="text-lg font-semibold mb-1">DSM-5 ASD Criteria</h3>
              <p className="text-xs text-gray-500 mb-4">
                For each criterion, record whether the MDT agrees the threshold is met.
                These are captured for the record and are optional.
              </p>

              <div className="border border-gray-300 rounded-lg overflow-hidden">
                {/* Table header */}
                <div className="grid grid-cols-[88px_1fr_120px] bg-gray-200 text-xs font-bold text-gray-700 uppercase tracking-wide">
                  <div className="px-3 py-2.5 border-r border-gray-300">Criterion</div>
                  <div className="px-3 py-2.5 border-r border-gray-300">Description</div>
                  <div className="px-3 py-2.5 text-center">Met threshold</div>
                </div>

                {ASD_CRITERIA_TABLE.map((section, sIdx) => (
                  <React.Fragment key={sIdx}>
                    {/* Section threshold header row */}
                    <div className="grid grid-cols-1 bg-gray-100 border-t border-gray-300">
                      <div className="px-3 py-2 text-xs font-bold text-gray-700">
                        {section.sectionHeader}
                      </div>
                    </div>

                    {section.criteria.map((criterion) => {
                      const value = criteria[criterion.id];
                      return (
                        <div
                          key={criterion.id}
                          className="grid grid-cols-[88px_1fr_120px] border-t border-gray-300"
                        >
                          {/* Criterion ID -- vertically centred */}
                          <div className="px-3 py-3 border-r border-gray-300 flex items-center justify-center">
                            <span className="text-sm font-bold text-gray-800">{criterion.label}</span>
                          </div>

                          {/* Description */}
                          <div className="px-3 py-3 border-r border-gray-300">
                            <p className="text-xs text-gray-700 leading-relaxed">{criterion.description}</p>
                          </div>

                          {/* Met threshold: separate Yes / No buttons */}
                          <div className="px-2 py-3 flex items-center justify-center gap-1.5">
                            {[{ v: true, label: 'Yes' }, { v: false, label: 'No' }].map(({ v, label }) => {
                              const isActive = value === v;
                              const activeClass = v
                                ? 'bg-emerald-100 border-emerald-400 text-emerald-700'
                                : 'bg-red-100 border-red-400 text-red-700';
                              return (
                                <button
                                  key={label}
                                  type="button"
                                  disabled={!canEdit}
                                  onClick={() => handleToggleCriterion(criterion.id, v)}
                                  className={`w-12 py-1 rounded-lg border-2 text-xs font-semibold transition-all duration-200 ${
                                    isActive
                                      ? activeClass
                                      : `bg-white border-gray-300 text-gray-500 ${canEdit ? 'hover:border-gray-400' : 'cursor-default'}`
                                  }`}
                                  aria-label={`${criterion.label} ${label}`}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Outcome selector */}
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 text-left">
              <h3 className="text-lg font-semibold mb-1">
                Diagnostic Outcome<span className="text-red-500 ml-0.5">*</span>
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Does the person meet the full DSM-5 diagnostic criteria for Autism Spectrum Disorder?
              </p>
              <div className="flex items-center gap-3">
                {[
                  { v: 'Met', label: 'Met', active: 'bg-emerald-100 border-emerald-400 text-emerald-700' },
                  { v: 'Not met', label: 'Not met', active: 'bg-red-100 border-red-400 text-red-700' },
                ].map(({ v, label, active }) => {
                  const isActive = outcome === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => handleSetOutcome(v)}
                      className={`flex-1 py-3 rounded-lg border-2 text-sm font-bold transition-all duration-200 ${
                        isActive
                          ? active
                          : `bg-white border-gray-300 text-gray-500 ${canEdit ? 'hover:border-gray-400' : 'cursor-default'}`
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          </div>

          <ModularStepFooter
            showBack
            onBack={() => navigateToSection(orderedIds.indexOf('mdtReview'))}
            counterText={`2 of ${navItems.length}`}
            onNext={handleNext}
            nextDisabled={!isValid}
            nextLabel={hasOther ? 'Next: Other Conditions' : 'Next: Final Formulation'}
          />
        </div>
      </div>
    </Layout>
  );
}
